import {AttrsSchema, Component} from 'panel';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {filterAttributes, getOwnAttr} from 'common/util';
import * as colors from 'common/util/colors';
import {getModifierKeys, KEY_VALUES, propagateEvent} from 'common/util/dom';
import {lexicalCompose} from 'common/util/function';
import {fuzzyMatch} from 'common/util/fuzzy-match';

import css from './index.styl?inline';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import type {
  CustomSearchMatcherFn,
  ElementRefs,
  ExpanderItemClickEvent,
  ItemClickEvent,
  ItemFocusEvent,
  ItemsMenuAttrs,
  ItemsMenuJsxProps,
  ItemsMenuState,
  Section,
  SectionItem,
  SectionItemContextPanelTransform,
  SectionsFromSearchInputFn,
  WrappedSection,
  WrappedSectionItem,
} from './types';
import {
  MAX_CSV_MATCHES,
  PROGRESSIVE_SIZE,
  SELECT_ALL_ITEM,
  SELECT_ALL_MATCHING_ITEM,
  SelectionState,
  SHOW_ALL_ITEM,
  SHOW_HIDDEN_SECTION_ITEM,
} from './constants';
import {alignDropdown, findSelectedItems, itemHasSubSections} from './utils';

export class ItemsMenu extends Component<ItemsMenuState, ItemsMenuAttrs> {
  private _keydownProxyEl = null;
  private showScrollShadow = false;
  private elRefs: ElementRefs = {};
  static get attrsSchema(): AttrsSchema<ItemsMenuAttrs> {
    return {
      ...super.attrsSchema,
      'disable-search-sort': {
        type: `boolean`,
        description: `Prevents search results from being sorted by this component.`,
      },
      'empty-message': `string`,
      'empty-search-results-message': {
        type: `string`,
        default: `Your search returned no results`,
        description: `Message to show when a filter results in an empty list`,
      },
      multiple: {type: `boolean`, description: `set "true" to render multi-checkbox selection`},
      'search-exact': {type: `boolean`, description: `only match search-filter substrings, don't fuzzy-match`},
      'search-filter': `string`,
      sections: {type: `json`, default: [], tsType: `Section[]`},
      'sections-hint': `string`,
      'show-csv-matches': `boolean`,
      'show-select-all': `boolean`,
      'show-select-all-matching': `boolean`,
      'select-all-disabled': `boolean`,
      'select-all-matching-disabled': `boolean`,
      'select-all-tooltip': `json`,
      'enforce-limit-when-searching': {type: `boolean`, description: `Use the "showLimit" even when searching`},
      'unfocus-on-mouseleave': `boolean`,
      'align-all-text': {
        type: `boolean`,
        description: `align text whether icons are present or not. use when you have icons sparsely spread through the menu`,
      },
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        contextPanelTransform: null,
        customSearchMatcherFn: null,
        focusedItemIndex: 0,
        fullyShownSections: {},
        isPillFocused: false,
        isSubItemsOpen: false,
        keyedItems: {},
        progressiveListSize: PROGRESSIVE_SIZE,
        sectionsFromInputFn: null,
        sections: [],
        selectionState: SelectionState.None,
        tooltipData: null,
      },

      helpers: {
        propagateEvent: (ev) => propagateEvent(this, ev),
        filterAttributes,
        getDisplaySections: () => this.getDisplaySections(),
        getElRef: (ref: string) => this.elRefs[ref],
        setElRef: (ref: string, el: HTMLElement) => {
          this.elRefs[ref] = el;
          this.update({});
        },

        getDropdownPositioning: (ref: string, {addVerticalOffset = true} = {}) => {
          const elref = this.helpers.getElRef(ref);
          const selectedEl = this.getFocusedItemEl();
          if (!elref || (addVerticalOffset && !selectedEl)) {
            return {
              styles: {visibility: `hidden`},
            };
          }

          const parentRect = this.getBoundingClientRect();
          const containerRect = elref.getBoundingClientRect();
          const focusedItemVerticalOffset = addVerticalOffset
            ? selectedEl.getBoundingClientRect().top - parentRect.top
            : 0;
          const position = alignDropdown(parentRect, containerRect, focusedItemVerticalOffset);

          return {
            horizontal: position.horizontal,
            vertical: position.vertical,
            styles: {
              ...(position.vertical === `top` ? {top: `${focusedItemVerticalOffset}px`} : {bottom: `0px`}),
            },
          };
        },

        handleSubItemsClickItem: (ev) => this.handleSubItemsClickItem(ev),
        handleSubItemsNavigateBack: () => this.setSubItemsIsOpen(false),
        handleScroll: (ev) => this.handleScroll(ev),
        handlePostPatch: () => {
          // When this component is patched, the height of it could change. Send a draw event so users of the
          // component can respond to the height change accordingly.
          this.dispatchCustomEvent(`draw`);
        },
        handleClickItem: (item, ev) => this.handleClickItem(item, getModifierKeys(ev)),
        handleMouseenterItem: (item: WrappedSectionItem) => {
          this.update({isPillFocused: false});
          this.focusItem(item.index, {canShowContextPanel: true, scrollIntoView: false, canShowSubItems: true});
        },
        handleMouseleaveItem: (item: WrappedSectionItem) => {
          if (this.attr(`unfocus-on-mouseleave`) && item.index === this.state.focusedItemIndex) {
            this.setSubItemsIsOpen(false);
            this.update({
              focusedItemIndex: -1,
              tooltipData: null,
            });
          }
        },
        handleMouseenterPill: () => this.update({isPillFocused: true}),
        handleMouseleavePill: () => this.update({isPillFocused: false}),
        handleClickPill: (item, ev) => {
          // Pill click automatically bubbles up and becomes item click. We don't want that
          if (ev) {
            ev.stopPropagation();
          }

          this.handleClickPill(item, getModifierKeys(ev));
        },
      },
    };
  }

  constructor() {
    super();
    // Since handlers can be called on proxy elements, use bind for consistent `this`
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.attr(`unfocus-on-mouseleave`)) {
      this.update({focusedItemIndex: -1}); // no item is focused by default
    }
    this.addEventListener(`keydown`, this.handleKeydown, true);
    window.addEventListener(`resize`, this.handleResize);
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    super.disconnectedCallback();
    window.removeEventListener(`resize`, this.handleResize);
    this.removeEventListener(`keydown`, this.handleKeydown, true);
    this.keydownProxyEl = null;
  }

  handleInsertScreenListContainer(elm) {
    this.helpers.setElRef(`containerEl`, elm);
  }

  dispatchCustomEvent(name, opts = {}) {
    this.dispatchEvent(new CustomEvent(name, opts));
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `sections`) {
      this.sections = this.attr(`sections`);
    } else if (attr === `search-filter` && newVal === `` && oldVal !== ``) {
      this.update({fullyShownSections: {}});
      this.focusItem(0);
    } else if (attr === `search-filter` && this.state.focusedItemIndex > 0) {
      // Searching resets focus to first item which the user is most likely to select
      this.focusItem(0);
    }
  }

  /**
   *  Handles arrow and tab keydown events from domEl for keyboard accessibility
   */
  set keydownProxyEl(domEl: HTMLElement) {
    if (this._keydownProxyEl) {
      this._keydownProxyEl.removeEventListener(`keydown`, this.handleKeydown, true);
    }
    this._keydownProxyEl = domEl;
    if (this._keydownProxyEl) {
      this._keydownProxyEl.addEventListener(`keydown`, this.handleKeydown, true);
    }
  }

  get keydownProxyEl() {
    return this._keydownProxyEl;
  }

  /**
   * Function that returns a new section to be added depending on user input
   */
  set sectionsFromInputFn(sectionsFromInputFn: SectionsFromSearchInputFn) {
    this.update({sectionsFromInputFn});
  }

  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(contextPanelTransform: SectionItemContextPanelTransform) {
    this.update({contextPanelTransform});
  }

  set customSearchMatcherFn(customSearchMatcherFn: CustomSearchMatcherFn) {
    this.update({customSearchMatcherFn});
  }

  /**
   *  sections passed as a prop for perf purposes when passing large number of items.
   *  JSON serialization/deserialization can have a noticeable perf impact.
   */
  set sections(sections: Section[]) {
    // Gracefully handle bad data from consumers, some items are being passed without a label
    const keyedItems = keyBy(sections.map(({items}) => items).flat(), ({label = ``}) => label.toLowerCase().trim());

    this.update({
      sections,
      selectionState: this.getSelectionState(sections),
      keyedItems,
    });
  }

  setSubItemsIsOpen(enabled: boolean) {
    this.update({isSubItemsOpen: enabled});
  }

  handleKeydown(ev) {
    const {isPillFocused, isSubItemsOpen} = this.state;
    const preventAction = () => {
      ev.stopPropagation();
      ev.preventDefault();
    };

    /** when the list has an active sub list, delegate keyboard actions to the nested mp-items-menu */
    if (isSubItemsOpen) {
      return;
    }

    switch (ev.code) {
      case KEY_VALUES.ARROW_UP:
        this.focusItem(Math.max(this.state.focusedItemIndex - 1, 0), {canShowContextPanel: true});
        preventAction();
        break;
      case KEY_VALUES.ARROW_DOWN:
        this.focusItem(
          Math.min(
            this.state.focusedItemIndex + 1,
            this.countTotalItems(this.getFilteredSections(this.state.sections)) - 1,
          ),
          {canShowContextPanel: true},
        );
        preventAction();
        break;
      case KEY_VALUES.ARROW_LEFT: {
        this.setSubItemsIsOpen(false);
        this.dispatchCustomEvent(`navigateBack`);
        break;
      }
      case KEY_VALUES.ARROW_RIGHT: {
        const [focusedItem] = this.getFocusedItem();
        if (itemHasSubSections(focusedItem)) {
          this.setSubItemsIsOpen(true);
          preventAction();
        }
        break;
      }
      case KEY_VALUES.TAB: {
        // We should never be swallowing tab presses since they are used by browser to switch focus
        // however to preserve backwards compatibility, we only swallow tab presses if item has pill
        const [focusedItem] = this.getFocusedItem();
        if (focusedItem?.hasPill) {
          this.update({isPillFocused: !isPillFocused});
          preventAction();
        }
        break;
      }
      case KEY_VALUES.ENTER: {
        // user can press enter even though are no items or search results are empty
        const [focusedItem] = this.getFocusedItem();
        if (focusedItem && !focusedItem.isDisabled && !focusedItem.isReadOnly) {
          if (isPillFocused) {
            this.handleClickPill(focusedItem, getModifierKeys(ev));
          } else {
            this.handleClickItem(focusedItem, getModifierKeys(ev));
          }
        }
        preventAction();
        break;
      }
      default:
        break;
    }
  }

  handleScroll(ev: Event) {
    const target = ev.target as HTMLElement;
    const PROGRESSIVE_LIST_BUFFER_PX = 250;
    const scrollBottom = target.scrollTop + target.offsetHeight;
    if (scrollBottom + PROGRESSIVE_LIST_BUFFER_PX >= target.scrollHeight) {
      this.increaseProgressiveListSize();
    }
    this.showScrolledTopShadow(target.scrollTop > 0);
  }

  handleResize(_ev) {
    this.update({});
  }

  showScrolledTopShadow(showScrollShadow) {
    if (this.showScrollShadow !== showScrollShadow) {
      // top shadow is a dynamic host style, therefore we need to use dom style api
      this.style.boxShadow = showScrollShadow ? `inset 0px 6px 10px -10px ${colors.gray2}` : `none`;
      this.showScrollShadow = showScrollShadow;
    }
  }

  getFocusedItemEl() {
    return this.el.querySelector<HTMLElement>(`.list-option.focused`);
  }

  scrollToItemIfNeeded() {
    window.requestAnimationFrame(() => {
      const listEl = this.elRefs.containerEl;
      const focusedEl = this.getFocusedItemEl();
      if (!focusedEl || !this.elRefs.containerEl) {
        return;
      }

      // The top threshold should be large enough to account for section header
      const THRESHOLD = 45;
      const listElTop = listEl.scrollTop;
      const listElBottom = listElTop + listEl.offsetHeight;
      const focusedElTop = focusedEl.offsetTop - listEl.offsetTop;
      const focusedElBottom = focusedElTop + focusedEl.offsetHeight;
      if (focusedElTop - THRESHOLD < listElTop) {
        listEl.scrollTop = focusedElTop - THRESHOLD;
      } else if (focusedElBottom + THRESHOLD > listElBottom) {
        listEl.scrollTop = focusedElBottom + THRESHOLD - listEl.offsetHeight;
      }
    });
  }

  /** For mnemonic focus. Find item with label that matches prefix and focus on it */
  focusItemWithPrefix(prefix) {
    const item = this.findSelectedItem((item) => item.label.toLowerCase().startsWith(prefix.toLowerCase()));

    if (item) {
      this.focusItem(item.index);
    }
  }

  focusItemWithLabel(label) {
    const item = this.findSelectedItem((item) => item.label.toLowerCase() === label.toLowerCase());

    if (item) {
      this.focusItem(item.index);
    }
  }

  focusSelectedItem() {
    const item = this.findSelectedItem((item) => item.isSelected);

    if (item) {
      this.focusItem(item.index);
    }
  }

  findSelectedItem(predicate: (item) => boolean) {
    const sections = this.getDisplaySections();
    const items = findSelectedItems(sections, predicate) as WrappedSectionItem[];

    return items.length ? items[0] : null;
  }

  focus() {
    this.el.querySelector<HTMLElement>(`.screen-list-container`).focus();
  }

  handleClickItem(item: WrappedSectionItem, modifierKeys) {
    const {selectionState, sections} = this.state;
    const eventDetail = {
      selectionState,
      modifierKeys,
    };

    if (item.isSelectAll) {
      this.dispatchCustomEvent(`selectAll`, {detail: eventDetail});
    } else if (item.isSelectAllMatching) {
      const searchFilter = this.attr(`search-filter`);
      const matchingSections = this.getMatchedSections(searchFilter, this.getWrappedSections(sections));

      const trimmedMatchingSections = matchingSections.map((section) => ({
        ...omit(section, `items`, `wrappedSectionIndex`),
        items: section.items.map((item) => this.unwrapItem(item)),
      }));

      this.dispatchCustomEvent(`selectAllMatching`, {
        detail: {matchingSections: trimmedMatchingSections},
      });
    } else if (item.isExpander) {
      this.handleShowAllItemClick(item);
    } else if (!item.isDisabled && !itemHasSubSections(item)) {
      const [, section] = this.getFocusedItem(this.state.focusedItemIndex);
      const itemClickEvent: ItemClickEvent = new CustomEvent(`clickItem`, {
        detail: {
          ...eventDetail,
          ...item,
          section,
          searchFilter: this.attr(`search-filter`),
          item: this.unwrapItem(item),
          topScrollPosition: this.elRefs.containerEl?.scrollTop,
        },
      });
      this.dispatchEvent(itemClickEvent);
    }
  }

  handleSubItemsClickItem(ev: CustomEvent) {
    const [focusedItem] = this.getFocusedItem();
    const parentItem = omit(this.unwrapItem(focusedItem), `subSections`);
    const detail = {
      ...ev.detail,
      parentItems: [parentItem, ...(ev.detail.parentItems || [])],
    };

    this.dispatchCustomEvent(ev.type, {detail});
  }

  handleClickPill(item, modifierKeys) {
    const detail = {item: this.unwrapItem(item), modifierKeys};
    this.dispatchCustomEvent(`clickPill`, {detail});
  }

  handleShowAllItemClick(item: WrappedSectionItem) {
    const sectionIndex = item.sectionIndex;
    const fullyShownSections = this.state.fullyShownSections;
    fullyShownSections[sectionIndex] = true;
    this.update(fullyShownSections);

    const section = this.state.sections[item.sectionIndex];
    const clickExpanderItemEvent: ExpanderItemClickEvent = new CustomEvent(`clickExpanderItem`, {
      detail: {section, sectionIdx: sectionIndex},
    });
    this.dispatchEvent(clickExpanderItemEvent);
  }

  focusItem(
    focusedItemIndex: number,
    {canShowContextPanel = false, scrollIntoView = true, canShowSubItems = false} = {},
  ) {
    const {contextPanelTransform} = this.state;
    const [focusedItem, focusedSetion] = this.getFocusedItem(focusedItemIndex);
    const item = this.unwrapItem(focusedItem);
    this.dispatchEvent(new CustomEvent(`focusItem`, {detail: {item, section: focusedSetion}}) as ItemFocusEvent);
    this.setSubItemsIsOpen(itemHasSubSections(item) ? canShowSubItems : false);
    this.update({
      focusedItemIndex,
      tooltipData: null,
    });

    if (canShowContextPanel && contextPanelTransform && !item.isDisabled) {
      this.update({
        tooltipData: item && !itemHasSubSections(item) ? contextPanelTransform(item) : null,
      });
    }

    if (scrollIntoView) {
      this.scrollToItemIfNeeded();
    }
  }

  /** returns the focused item and section */
  getFocusedItem(focusedItemIndex = this.state.focusedItemIndex): [WrappedSectionItem, Section] {
    for (const section of this.getFilteredSections(this.state.sections)) {
      for (const item of section.items) {
        if (focusedItemIndex === item.index) {
          return [item, omit(section, `items`, `wrappedSectionIndex`)];
        }
      }
    }

    return [null, null];
  }

  wrapItem(item: WrappedSectionItem | SectionItem, index: number = null): WrappedSectionItem {
    return {
      ...item,
      index,
      wrapped: getOwnAttr(item, `wrapped`) ?? item,
    };
  }

  unwrapItem(item: WrappedSectionItem) {
    return item?.wrapped;
  }

  getSelectionState(sections: Section[]) {
    const totalCount = this.countTotalItems(sections);
    const selectedCount = sections.reduce(
      (count, section) => count + section.items.filter((item) => item.isSelected).length,
      0,
    );

    switch (selectedCount) {
      case 0:
        return SelectionState.None;
      case totalCount:
        return SelectionState.All;
      default:
        return SelectionState.Some;
    }
  }

  getDisplaySections() {
    let sections = this.getFilteredSections(this.state.sections);
    // sections = this.getVisibleSections(sections);
    sections = this.getProgressiveCappedSections(sections);
    sections = this.getFilledSections(sections);
    return sections;
  }

  /**
   * TODO(pablo): for completeness, nested sections should come up in the search results
   */
  getFilteredSections(sourceSections: Section[]): WrappedSection[] {
    const searchFilter = this.attr(`search-filter`);
    // Pass sections through a bunch of pure transforms and filter functions
    let sections = this.getWrappedSections(sourceSections);
    if (searchFilter) {
      sections = this.getMatchedSections(searchFilter, sections);
      sections = this.getHighlightedSections(searchFilter, sections);
      sections = this.appendHiddenSectionExpander(sections);
      sections = this.appendSectionFromInput(searchFilter, sections);
      sections = this.appendBulkSelectSection(searchFilter, sections);
      if (this.attr(`enforce-limit-when-searching`)) {
        sections = this.getShowLimitCappedSections(sections);
      }
    } else {
      // hidden sections only show up in search
      sections = this.getVisibleSections(sections);
      sections = this.appendBulkSelectSection(searchFilter, sections);
      // show limited sections only apply when user isn't searching
      sections = this.getShowLimitCappedSections(sections);
    }
    sections = this.getWrappedSections(sections);

    return sections;
  }

  // Append section only if there is no exact match
  canAppendSectionFromInput(searchFilter: string, sections: WrappedSection[]) {
    for (const section of sections) {
      for (const item of section.items) {
        if (item.label === searchFilter) {
          return false; // exact match, item already exists, terminate early.
        }
      }
    }
    return true;
  }

  getSectionFromCSVSearch(searchFilter: string) {
    const tokens = searchFilter.split(`,`);
    if (tokens.length > MAX_CSV_MATCHES) {
      return [];
    }
    const items = tokens
      .map((token) => {
        const trimmed = token?.trim();
        const key = trimmed?.toLowerCase();
        if (!trimmed) {
          return null;
        }
        return this.state.keyedItems[key] || {label: trimmed, value: trimmed, isSelected: false};
      })
      .filter(Boolean); // Filters out empty strings and any other falsey value

    return [
      ...this.getWrappedSections([
        {
          items,
        },
      ]),
    ];
  }

  appendSectionFromInput(searchFilter: string, sections: WrappedSection[]) {
    const sectionsFromInputFn = this.state.sectionsFromInputFn;
    const newSection = sectionsFromInputFn && sectionsFromInputFn(searchFilter, sections);
    if (newSection && this.canAppendSectionFromInput(searchFilter, sections)) {
      return [...sections, ...this.getWrappedSections(newSection)];
    }

    return sections;
  }

  // Adds a virtual section or item for a select all option when in multiple mode
  appendBulkSelectSection(searchFilter: string, sections: WrappedSection[]) {
    const {selectionState} = this.state;
    const totalCount = this.countTotalItems(sections);

    const addSelectAll = this.attr(`show-select-all`) && !searchFilter;
    const addSelectAllMatching = this.attr(`show-select-all-matching`) && searchFilter;

    if (this.attr(`multiple`) && (addSelectAll || addSelectAllMatching) && totalCount > 0) {
      sections = [...sections];
      const isDisabled = this.attr(`select-all-disabled`);

      const bulkSelectItem = addSelectAll
        ? this.wrapItem({
            ...SELECT_ALL_ITEM,
            isDisabled,
            isSelected: selectionState === SelectionState.All,
            tooltip: this.attr(`select-all-tooltip`),
          })
        : this.wrapItem({
            ...SELECT_ALL_MATCHING_ITEM,
            isDisabled: this.attr(`select-all-matching-disabled`),
            isSelected: this.allItemsSelected(sections),
          });

      // if there is only a single section, then 'Select All' is added as the first
      // item, otherwise 'Select All' is added as the first section on its own.
      if (sections.length === 1) {
        sections[0] = {
          ...sections[0],
          items: [bulkSelectItem, ...sections[0].items],
        };
      } else {
        sections.unshift({items: [bulkSelectItem]});
      }
    }

    return sections;
  }

  appendHiddenSectionExpander(sections: WrappedSection[]) {
    return sections.map((section, sectionIndex) => {
      if (section.isHidden && !this.state.fullyShownSections[sectionIndex] && section.items.length > 0) {
        return {
          ...section,
          items: [
            this.wrapItem({
              ...SHOW_HIDDEN_SECTION_ITEM,
              sectionIndex,
              label: `Show ${section.label} (${section.items.length})`,
            }),
          ],
        };
      }

      return section;
    });
  }

  allItemsSelected(sections: WrappedSection[]): boolean {
    return sections.every((section) => section.items.every((section) => section.isSelected));
  }

  getWrappedSections(sections: Section[]): WrappedSection[] {
    let itemIndex = 0;

    return sections.map((section, sectionIndex) => ({
      ...section,
      wrappedSectionIndex: (section as WrappedSection).wrappedSectionIndex ?? sectionIndex,
      items: section.items.map((item) => this.wrapItem(item, itemIndex++)),
    }));
  }

  getMatchedSections(searchFilter: string, sections: WrappedSection[]): WrappedSection[] {
    if (this.attr(`show-csv-matches`) && searchFilter.includes(`,`)) {
      return this.getSectionFromCSVSearch(searchFilter);
    } else {
      return sections.map((section) => ({...section, items: this.getMatchingItems(searchFilter, section.items)}));
    }
  }

  getHighlightedSections(searchFilter: string, sections: WrappedSection[]): WrappedSection[] {
    return sections.map((section) => ({...section, items: this.getHighlightedItems(searchFilter, section.items)}));
  }

  getShowLimitCappedSections(sections: WrappedSection[]) {
    const fullyShownSections = this.state.fullyShownSections;
    return sections.map((section, idx) => {
      const showLimit = section.showLimit;
      let items = section.items;
      // Add a `show all (x)` item at the end of section and limit number of items displayed
      if (showLimit > 0 && items.length > showLimit && !fullyShownSections[idx]) {
        const showAllItem = {...SHOW_ALL_ITEM, sectionIndex: idx, label: `${SHOW_ALL_ITEM.label} (${items.length})`};
        items = [...items.slice(0, showLimit), this.wrapItem(showAllItem)];
      }
      return {...section, items};
    });
  }

  getProgressiveCappedSections(sections: WrappedSection[]) {
    const progressiveListSize = this.state.progressiveListSize;
    return sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => item.index < progressiveListSize),
    }));
  }

  getFilledSections(sections: WrappedSection[]) {
    const searchFilter = this.attr(`search-filter`);
    // Loading section only shown in non search scenarios
    return sections.filter((section) => section.items.length > 0 || (!searchFilter && section.isLoading));
  }

  countTotalItems(sections: Section[]) {
    return sections.reduce((numItems, section) => numItems + section.items.length, 0);
  }

  sortOnProperty(matchProp) {
    const getMatch = (item) => (item[matchProp] ? item[matchProp] : {score: -1, matchStr: ``});

    return lexicalCompose(
      // fuzzy match score
      (a, b) => getMatch(b).score - getMatch(a).score,
      // alphabetical
      (a, b) => getMatch(a).matchStr.localeCompare(getMatch(b).matchStr),
    );
  }

  getMatchingItems(searchFilter: string, items: WrappedSectionItem[]): WrappedSectionItem[] {
    const res = this.getHighlightedItems(searchFilter, items).filter(
      (item) => !!item.labelMatch || !!item.subLabelMatch || !!item.customMatch,
    );

    if (!this.attr(`disable-search-sort`)) {
      res.sort(
        lexicalCompose(
          (a, b) => b.isExactLabelMatch - a.isExactLabelMatch,
          this.sortOnProperty(`labelMatch`),
          this.sortOnProperty(`subLabelMatch`),
          this.sortOnProperty(`customMatch`),
        ),
      );
    }

    return res;
  }

  getHighlightedItems(searchFilter: string, items: WrappedSectionItem[]): WrappedSectionItem[] {
    if (this.attr(`search-exact`)) {
      searchFilter = `"` + searchFilter + `"`;
    }

    const searchFilterLowerCase = searchFilter.toLowerCase();

    return items.map((item) => ({
      ...item,
      isExactLabelMatch: item.label.toLowerCase() === searchFilterLowerCase,
      labelMatch: fuzzyMatch(item.label, searchFilter),
      subLabelMatch: fuzzyMatch(item.subLabel, searchFilter),
      customMatch: this.state.customSearchMatcherFn?.(item, searchFilter),
    }));
  }

  getVisibleSections(sections: WrappedSection[]): WrappedSection[] {
    return sections.filter((section) => !section.isHidden);
  }

  increaseProgressiveListSize() {
    let progressiveListSize = this.state.progressiveListSize;
    if (progressiveListSize < this.countTotalItems(this.getFilteredSections(this.state.sections))) {
      progressiveListSize += PROGRESSIVE_SIZE;
      this.update({progressiveListSize});
    }
  }
}

export const ItemsMenuEl = defineAndMakeJsxFactory<ItemsMenuJsxProps>(`mp-items-menu`, ItemsMenu);
